import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone'
import { AnimatePresence } from 'framer-motion'
import { useSelector } from 'react-redux'

import { Check, RichText, Schedule, Property, Icon, Animation } from 'components'
import { TagBlock } from 'features'
import { itemContext, sectionContext, tagContext, clientContext } from 'context'
import { FaCalendar, FaCheckDouble } from 'react-icons/fa'

import { scheduleString } from 'utils'

const defaultStyle = {
    backgroundColor: 'var(--primary-color)',
    secondaryColor: 'var(--primary-accent2-color)',
    width: '20rem',
    margin: '0.5rem 0',
    boxShadowColor: 'var(--box-shadow)',
}

const TextContainer = styled.div`
    position: relative;
    padding: ${props => props.hasProperties ? '0.4rem 0.5rem 0 0.2rem' : '0.5rem 0.5rem 0.3rem 0'};
    z-index: 0;
    width: 100%;
    height: auto;
    overflow: visible;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    color: ${props => props.completed ? 'var(--secondary-accent-color)' : 'var(--text-color)'};
    transition: all 0.3s cubic-bezier(.55, 0, .1, 1);

    @media not all and (min-resolution:.001dpcm) { 
        -webkit-text-decoration-line: ${props => props.completed ? 'line-through' : null};
    };
    
    @media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution:.001dpcm) {
        text-decoration: ${props => props.completed ? 'line-through' : null};
        text-decoration-color: var(--secondary-accent-color);
        // text-decoration-thickness: 1rem; // Will cause issues for Firefox
    };
`

const PropertiesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    grid-column: 2;
`

const Wrapper = styled.div`
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    width: ${props => props.offset ? `calc(100% - ${props.offset * 32}px)` : props.styles.width || '100%'};
    min-height: 1.5rem;
    position: relative;
    padding: 0.5rem 0.5rem;
    overflow: hidden;
    background-color: ${props => props.styles.backgroundColor};
    box-shadow: ${props => props.styles.boxShadow};
    margin: ${props => props.styles.margin};
`

const Container = styled.div`
    width: 100%;
    display: grid;
    grid-column-gap: 0.5rem;
    grid-template-columns: 1rem auto;
    grid-template-rows: auto auto;
`

const PrimaryButtons = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    grid-row: 1 / 2;
    align-self: start;
    padding-left: 0.5rem;
`

const PageTitle = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    grid-column-gap: 0.2rem;
    align-items: center;
`

const ItemPreview = props =>  {
    const {
        item,
        animateProps,
        styles
    } = props;
    const user = useSelector((state) => state.auth.user)
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())

    // Constants //
    const completed = item['status'] === itemContext.STATUS.COMPLETED
    const updatedStyle = Object.assign({}, defaultStyle, styles)

    // Elements //
    const primaryButtons = <PrimaryButtons>
                                <Check filled={!!completed} onSelect={() => {}}
                                    color={!completed ? itemContext.PRIORITY_COLOR[item['priority']] : null}
                                    backgroundColor={completed ? itemContext.PRIORITY_COLOR[item['priority']] : null}
                                    borderColor={itemContext.PRIORITY_COLOR[item['priority']]}/>
                            </PrimaryButtons>
    
    let properties = []
    // Client item label
    const client = item['client']
    client && properties.push(
        <Property key={uuidv4()} 
            img={clientContext[client]['icon']['value']}
            tooltip={item['clientTitle']}
        />
    )

    // Page name
    const page = item['pageID'] && data['pages'][item['pageID']]
    if(page){
        const pageTitle = <PageTitle>
                        <Icon size={'1rem'} styles={{padding: 0}} value={page.icon ? page.icon.value : null} type={page.icon ? page.icon.type : null}/>
                        {page['page_name']}
                    </PageTitle>
        properties.push(<Property key={uuidv4()} label={pageTitle}/>)
    }

    // Children count
    if(item['children']){
        if(item['children']['INCOMPLETE'].length + item['children']['COMPLETED'].length > 0){
            properties.push(
                <Property icon={<FaCheckDouble/>} 
                                onSelect={() => {}}
                                label={`${item['children']['completed']}/${item['children']['total']}`}/>
            )
        }
    }

    // Dates
    const itemDates = {
        start_date: item['start_date'],
        end_date: item['end_date'],
        start_time: item['start_time'],
        end_time: item['end_time']
    }
    const timezone = moment.tz.guess()
    const schedule = scheduleString(itemDates, timezone);
    if(schedule.valid){
        const dates = {
            start_date: item.start_date ? moment(item.start_date).tz(timezone) : null,
            end_date: item.end_date ? moment(item.end_date).tz(timezone) : null, 
            start_time: item.start_time ? moment(item.start_time).tz(timezone) : null,
            end_time: item.end_time ? moment(item.end_time).tz(timezone) : null
        }
        properties.push(<Property key={uuidv4()}  
                            icon={<FaCalendar/>} tooltip='Schedule' 
                            label={<Schedule dates={{start: schedule.start, end: schedule.end}}/>}/>)
    }

    // Tags
    item['tags'] && item['tags'].map(tagID => {
        const tag = data['tags'][tagID]
        properties.push(<TagBlock tag={tag} style={{margin: '0.2rem'}}/>)
    })

    const textElement = 
    <TextContainer completed={completed} hasProperties={properties.length > 0}>
        <RichText value={item['content']} readOnly disableAutoUpdate
            styles={{
                primaryColor: updatedStyle['backgroundColor'],
                secondaryColor: updatedStyle['secondaryColor'],
                padding: 0,
                margin: 0
            }}/>
    </TextContainer>
    
    // Render // 
    return (
        <Wrapper offset={item['offset']} styles={updatedStyle}>
            <Container>
                {primaryButtons}
                {textElement}
                {/* {!edit && !this.props.disableButtons ? secondaryButtons : null} */}
                <span/>
                {animateProps ? 
                <AnimatePresence>
                    {properties.length > 0 && 
                        <Animation animation='expand' duration={0.5}>
                            <PropertiesContainer>{properties}</PropertiesContainer>
                        </Animation>}
                </AnimatePresence>
                : <PropertiesContainer>{properties}</PropertiesContainer>
            }
                
            </Container>
        </Wrapper>
    )
}


export default ItemPreview;
